import './index.css';
import 'katex/dist/katex.min.css';
import * as katex from 'katex';
import * as THREE from 'three';

// import { renderMathInElement } from 'katex/contrib/auto-render';
import { renderMathInElement } from './KaTeXAutoRender';

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { InteractionManager, InteractiveEvent } from './three.interactive';

import { Line2 } from 'three/examples/jsm/lines/Line2';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';

function memoize<Arg0, Result>(factory: (arg0: Arg0) => Result) {
	var cache: any = {};
	return function (key: Arg0): Result {
		if (!(key as any in cache)) {
			cache[key] = factory(key);
		}
		return cache[key]!;
	};
}

const colorToRgba = (function () {
	const canvas = document.createElement('canvas');
	canvas.width = canvas.height = 1;
	const ctx = canvas.getContext('2d')!;

	return memoize(function (col: string | CanvasGradient | CanvasPattern) {
		ctx.clearRect(0, 0, 1, 1);
		ctx.fillStyle = '#000';
		ctx.fillStyle = col;
		const computed = ctx.fillStyle;
		ctx.fillStyle = '#fff';
		ctx.fillStyle = col;
		if (computed !== ctx.fillStyle) { return 0xffff00ff; }
		ctx.fillRect(0, 0, 1, 1);
		const imageData = ctx.getImageData(0, 0, 1, 1).data;
		// swap required as THREE.js expects an ARGB little-endian int
		[imageData[0], imageData[2]] = [imageData[2], imageData[0]];
		return new Uint32Array(imageData.buffer)[0];
	});
})();

function range(start: number, stop?: number, step?: number) {
	if (stop === undefined) {
		stop = start;
		start = 0;
	}

	if (step === undefined) {
		step = 1;
	}

	if ((step > 0 && start >= stop) || (step < 0 && start <= stop)) {
		return [];
	}

	const result = [];
	for (var i = start; step > 0 ? i < stop : i > stop; i += step) {
		result.push(i);
	}

	return result;
}

function deBoor(k: number, x: number, t: number[], c: THREE.Vector3[], p: number) {
	const d = Array.from({ length: p + 1 }, (_, j) => c[j + k - p]);
	for (var r of range(1, p + 1)) {
		for (var j of range(p, r - 1, -1)) {
			const alpha = (x - t[j + k - p]) / (t[j + 1 + k - r] - t[j + k - p]);
			d[j] = d[j - 1].clone().lerp(d[j], alpha);
		}
	}
	return d[p];
}

function evaluateBSpline(c: THREE.Vector3[]) {
	const p = 3;
	const t: number[] = [];
	const cd: THREE.Vector3[] = [];
	for (var i = 0; i < p; i++) {
		t.push(0);
	}
	for (var i = 0; i < c.length; i++) {
		t.push(i);
		cd.push(c[i]);
	}
	for (var i = 0; i < p; i++) {
		t.push(c.length - 1);
		cd.push(c[c.length - 1]);
	}
	cd.pop();

	var positions: number[] = [];
	var k = p;
	for (var xi = 0; xi <= 128; xi++) {
		var x = (c.length - 1) * (xi / 128);
		if (t[k + 1] < x) {
			k++;
		}
		deBoor(k, x, t, cd, p).toArray(positions, positions.length);
	}

	return positions;
}

class InteractiveDiagram {
	readonly canvas: HTMLCanvasElement;
	readonly canvasContainer: HTMLElement;
	private headerRow: HTMLTableRowElement;
	private xRow: HTMLTableRowElement;
	private yRow: HTMLTableRowElement;
	private wRow: HTMLTableRowElement;
	private projectedHeaderRow: HTMLTableRowElement;
	private projectedXRow: HTMLTableRowElement;
	private projectedYRow: HTMLTableRowElement;
	private scene: THREE.Scene = new THREE.Scene();
	private renderer: THREE.WebGLRenderer;
	private foreground: number = colorToRgba(window.getComputedStyle(document.body).getPropertyValue('color'));
	private background: number = colorToRgba(window.getComputedStyle(document.documentElement).getPropertyValue('background-color'));
	private background2: number = colorToRgba(window.getComputedStyle(document.getElementsByClassName('content-wrapper')[0]).getPropertyValue('background-color'));
	private gridLineMats: LineMaterial[] = [];
	private gridLineMat2: LineMaterial = new LineMaterial({
		color: 0x7f7f7f,
		linewidth: 0.005,
		resolution: new THREE.Vector2(1.2, 0.8),
	});
	private controlPolyMat: LineMaterial = new LineMaterial({
		color: this.foreground,
		linewidth: 0.005,
		dashed: true,
		dashScale: 5,
		dashSize: 2,
		gapSize: 1,
		resolution: new THREE.Vector2(1.2, 0.8),
	});
	private homogeneousSplineMat: LineMaterial = new LineMaterial({
		color: this.foreground,
		linewidth: 0.005,
		dashed: true,
		dashScale: 10,
		dashSize: 1,
		gapSize: 1,
		resolution: new THREE.Vector2(1.2, 0.8),
	});
	private projectedSplineMat: LineMaterial = new LineMaterial({
		color: this.foreground,
		linewidth: 0.005,
		resolution: new THREE.Vector2(1.2, 0.8),
	});
	private projectionSurfaceMat: THREE.MeshStandardMaterial = new THREE.MeshStandardMaterial({
		color: this.foreground & 0xff00ff,
		roughness: 0.3,
		metalness: 0,
		opacity: 0.5,
		transparent: true,
		side: THREE.DoubleSide,
		depthWrite: false,
	});
	private light = new THREE.DirectionalLight(0xffffff, 0.9);
	private axisLabels: HTMLElement[] = [];
	private axisColors: number[] = [];
	private axisMaterials: THREE.MeshLambertMaterial[] = [];
	private sphereGeometry: THREE.IcosahedronGeometry = new THREE.IcosahedronGeometry(0.3, 1);
	private camera: THREE.OrthographicCamera = new THREE.OrthographicCamera(-6, 6, 6, -6, -100, 100);
	private controls: OrbitControls;
	private interaction: InteractionManager;

	private manipulators: THREE.Mesh[] = [];
	private currentNode: number = -1;
	private activeManipulator: THREE.Mesh | null = null;

	nodes: THREE.Vector3[] = [];
	private nodeRepresentations: {
		homogeneous: THREE.Mesh,
		projected: THREE.Mesh,
		projectionLine: Line2,
		transformedHomogeneous?: THREE.Mesh,
		transformedProjected?: THREE.Mesh,
		transformedProjectionLine?: Line2,
		tableHeader: HTMLElement,
		xCell: HTMLElement,
		yCell: HTMLElement,
		wCell: HTMLElement,
		projectedTableHeader: HTMLElement,
		projectedXCell: HTMLElement,
		projectedYCell: HTMLElement,
	}[] = [];
	// https://colorbrewer2.org/#type=qualitative&scheme=Set2&n=7
	private nodeColors: number[] = [
		0x66c2a5,
		0xfc8d62,
		0x8da0cb,
		0xe78ac3,
		0xa6d854,
		0xffd92f,
		0xe5c494,
	];

	private controlPolygon?: Line2;
	private homogeneousSpline?: Line2;
	private projectedSpline?: Line2;
	private projectionSurface?: THREE.Mesh;
	private allNodesInPlane: boolean = false;

	private _cameraControlsEnabled: boolean = true;
	private viewTable: HTMLTableElement;
	get cameraControlsEnabled() {
		return this._cameraControlsEnabled;
	}
	set cameraControlsEnabled(enabled: boolean) {
		this._cameraControlsEnabled = enabled;
		this.controls.enabled = enabled;
		if (enabled) {
			this.viewTable.removeAttribute('style');
		} else {
			this.viewTable.style.display = 'none';
		}
		const wRowEntries = this.wRow.getElementsByTagName('input');
		for (var i = 0; i < wRowEntries.length; i++) {
			const entry = wRowEntries.item(i)!;
			entry.disabled = !enabled;
		}
	}

	transform: THREE.Matrix3 = new THREE.Matrix3();
	private oldTransform = new THREE.Matrix3();
	private transformTable: HTMLTableElement;
	private transformInputs: HTMLInputElement[] = [];
	get showTransform() {
		return this.transformTable.style.display !== 'none';
	}
	set showTransform(enabled: boolean) {
		if (enabled) {
			this.transformTable.removeAttribute('style');
		} else {
			this.transformTable.style.display = 'none';
		}
	}

	constructor(canvas: HTMLCanvasElement) {
		// renderer
		this.canvas = canvas;
		this.canvasContainer = canvas.parentElement!;
		this.renderer = new THREE.WebGLRenderer({
			antialias: true,
			canvas: canvas,
		});
		this.renderer.setClearColor(this.background);

		// normal grid lines
		for (var axis = 0; axis < 3; axis++) {
			this.gridLineMats.push(
				new LineMaterial({
					color: this.background2,
					linewidth: 0.005,
					resolution: new THREE.Vector2(1.2, 0.8),
				})
			);
		}

		for (var axis = 0; axis < 3; axis++) {
			var i0 = 1;
			if (axis == 2) { i0 = 2; }
			for (var other = 1; other <= 2; other++) {
				const axis2 = (axis + other) % 3;
				const axis3 = (axis + 3 - other) % 3;
				for (var i = i0; i <= 10; i++) {
					const positions = [0, 0, 0, 0, 0, 0];
					positions[0 + axis] = i;
					positions[3 + axis] = i;
					positions[3 + axis2] = 10;
					const geometry = new LineGeometry();
					geometry.setPositions(positions)
					this.scene.add(new Line2(geometry, this.gridLineMats[axis3]))
				}
			}
		}

		// w = 1 grid lines
		for (var axis = 0; axis < 2; axis++) {
			for (var i = 0; i <= 10; i++) {
				const positions = [0, 0, 1, 0, 0, 1];
				positions[0 + axis] = i;
				positions[3 + axis] = i;
				positions[3 + (1 - axis)] = 10;
				const geometry = new LineGeometry();
				geometry.setPositions(positions)
				this.scene.add(new Line2(geometry, this.gridLineMat2))
			}
		}

		// lighting for axis arrows
		this.scene.add(new THREE.AmbientLight(0xffffff, 0.1));
		this.scene.add(this.light);
		this.scene.add(this.light.target);
		this.light.target.position.set(6, 6, 6);

		// axis arrow labels
		for (var name of ['X', 'Y', 'W']) {
			const label = document.createElement('div');
			label.innerText = name;
			label.classList.add('axis-label');
			label.classList.add('axis-label-' + name);
			this.canvasContainer.appendChild(label);
			this.axisColors.push(colorToRgba(window.getComputedStyle(label).getPropertyValue('color')));
			this.axisMaterials.push(new THREE.MeshLambertMaterial({ color: this.axisColors[this.axisColors.length - 1] }));
			this.axisLabels.push(label);
		}

		// axis arrows
		const arrowGeometry = new THREE.LatheGeometry([
			new THREE.Vector2(0, 0),
			new THREE.Vector2(0.12, 0),
			new THREE.Vector2(0.12, 0),
			new THREE.Vector2(0.12, 11),
			new THREE.Vector2(0.12, 11),
			new THREE.Vector2(0.30, 11),
			new THREE.Vector2(0.30, 11),
			new THREE.Vector2(0, 12),
		]);
		const yArrow = new THREE.Mesh(arrowGeometry, this.axisMaterials[1]);
		this.scene.add(yArrow);
		const xArrow = new THREE.Mesh(arrowGeometry, this.axisMaterials[0]);
		xArrow.rotateZ(-.5 * Math.PI);
		this.scene.add(xArrow);
		const zArrow = new THREE.Mesh(arrowGeometry, this.axisMaterials[2]);
		zArrow.rotateX(.5 * Math.PI);
		this.scene.add(zArrow);

		// origin sphere
		const gray = new THREE.MeshLambertMaterial({ color: 0x7f7f7f });
		const sphere = new THREE.Mesh(this.sphereGeometry, gray);
		this.scene.add(sphere);

		// manipulator arrows
		const manipulationArrowGeometry = new THREE.LatheGeometry([
			new THREE.Vector2(0, -1.5),
			new THREE.Vector2(0.30, -0.75),
			new THREE.Vector2(0.30, -0.75),
			new THREE.Vector2(0.12, -0.75),
			new THREE.Vector2(0.12, -0.75),
			new THREE.Vector2(0.12, 0.75),
			new THREE.Vector2(0.12, 0.75),
			new THREE.Vector2(0.30, 0.75),
			new THREE.Vector2(0.30, 0.75),
			new THREE.Vector2(0, 1.5),
		]);
		this.manipulators[0] = new THREE.Mesh(manipulationArrowGeometry, this.axisMaterials[0].clone());
		this.manipulators[0].rotateZ(-.5 * Math.PI);
		this.manipulators[1] = new THREE.Mesh(manipulationArrowGeometry, this.axisMaterials[1].clone());
		this.manipulators[2] = new THREE.Mesh(manipulationArrowGeometry, this.axisMaterials[2].clone());

		// camera and navigation
		this.camera.position.x = 12;
		this.camera.position.y = 12;
		this.camera.position.z = 12;

		this.camera.up.set(0, 0, 1);

		this.controls = new OrbitControls(this.camera, this.renderer.domElement);
		this.controls.enableDamping = true;
		this.controls.dampingFactor = 0.2;
		// this.controls.enablePan = false;
		this.controls.enableZoom = true;
		this.controls.minZoom = 1;
		this.controls.maxZoom = 8;
		this.controls.target = new THREE.Vector3(6, 6, 6);

		this.controls.saveState();

		// interaction manager
		this.interaction = new InteractionManager(this.renderer, this.camera, this.canvas);
		const enableControls = () => {
			this.controls.enabled = this._cameraControlsEnabled;
		};
		// window.addEventListener('focus', enableControls);
		if (!!window.PointerEvent) {
			this.canvas.addEventListener('pointerdown', (event) => {
				if (event.isPrimary) {
					this.canvas.setPointerCapture(event.pointerId);
				}
			});
			this.canvas.addEventListener('pointerup', (event) => {
				if (event.isPrimary) {
					this.canvas.releasePointerCapture(event.pointerId);
				}
			});
			window.addEventListener('pointerup', enableControls);
		}
		else {
			window.addEventListener('mouseup', enableControls);
			window.addEventListener('touchend', enableControls);
		}

		const originalNodePosition = new THREE.Vector3();
		const startPosition = new THREE.Vector3();
		for (var _i = 0; _i < 3; _i++) {
			const i = _i;
			const manipulator = this.manipulators[i];
			manipulator.visible = false;
			const manipulatorMaterial = (manipulator.material as THREE.MeshLambertMaterial);
			this.scene.add(manipulator);
			this.interaction.add(manipulator);

			const baseColor = this.axisMaterials[i].color;
			const foreground = new THREE.Color(this.foreground);
			const mixed = new THREE.Color();
			mixed.lerpColors(baseColor, foreground, 0.5);

			manipulator.addEventListener('mouseover', (e) => {
				const ie = e as InteractiveEvent;
				if (!manipulator.visible || manipulatorMaterial.opacity < 0.75 || this.activeManipulator !== null) {
					return;
				}
				ie.stopPropagation();
				manipulatorMaterial.color = mixed;
			});
			manipulator.addEventListener('mouseout', () => {
				if (this.activeManipulator === manipulator) {
					return;
				}
				manipulatorMaterial.color = baseColor;
			});
			manipulator.addEventListener('mousedown', (e) => {
				const ie = e as InteractiveEvent;
				if (!ie.intersected || !manipulator.visible || manipulatorMaterial.opacity < 0.75) {
					return;
				}
				ie.stopPropagation();
				this.controls.enabled = false;
				this.activeManipulator = manipulator;
				if (document.activeElement instanceof HTMLElement) {
					document.activeElement.blur();
				}

				originalNodePosition.copy(this.nodes[this.currentNode]);

				const raycaster = new THREE.Raycaster();
				raycaster.setFromCamera(ie.coords, this.camera);
				const intersection = raycaster.ray.origin.clone();
				intersection.addScaledVector(raycaster.ray.direction, ie.distance);

				startPosition.copy(intersection);
			});
			manipulator.addEventListener('mouseup', (e) => {
				const ie = e as InteractiveEvent;
				this.controls.enabled = this._cameraControlsEnabled;
				if (ie.intersected) {
					manipulatorMaterial.color = mixed;
				} else {
					manipulatorMaterial.color = baseColor;
				}
				this.activeManipulator = null;
			});
			manipulator.addEventListener('mousemove', (e) => {
				const ie = e as InteractiveEvent;
				if (this.activeManipulator !== manipulator) {
					return;
				}

				const axis = new THREE.Vector3();
				if (i < 2) {
					axis.setComponent(i, 1.0);
				} else {
					axis.copy(this.nodes[this.currentNode]);
					axis.normalize();
				}

				const raycaster = new THREE.Raycaster();
				raycaster.setFromCamera(ie.coords, this.camera);
				const cameraRay = raycaster.ray;

				const a = startPosition.clone();
				a.addScaledVector(axis, -20);
				const b = startPosition.clone();
				b.addScaledVector(axis, 20);

				const target = new THREE.Vector3();
				cameraRay.distanceSqToSegment(
					a, b, undefined, target
				);
				target.sub(startPosition);
				if (i < 2) {
					target.multiplyScalar(originalNodePosition.z);
				}
				target.add(originalNodePosition);
				if (target.z < 0.25) {
					target.copy(originalNodePosition);
					target.divideScalar(4.0 * target.z);
				}
				if (target.z > 20) {
					target.copy(originalNodePosition);
					target.divideScalar(1 / 20 * target.z);
				}
				target.x = Math.min(Math.max(0, target.x), 20);
				target.y = Math.min(Math.max(0, target.y), 20);
				this.nodes[this.currentNode].copy(target);
			});
		}

		// the dashed switch doesn't work alone but requires manually setting a define!
		this.controlPolyMat.defines.USE_DASH = '';
		this.controlPolyMat.needsUpdate = true;
		this.homogeneousSplineMat.defines.USE_DASH = '';
		this.homogeneousSplineMat.needsUpdate = true;

		this.transformTable = document.createElement('table');
		this.transformTable.style.display = 'none';
		this.transformTable.classList.add('transform-table');
		this.canvasContainer.insertAdjacentElement('afterend', this.transformTable);
		{
			const caption = document.createElement('caption');
			this.transformTable.appendChild(caption);
			caption.innerHTML = katex.renderToString('\\mathbf{A}_H');
			const transformRows: HTMLTableRowElement[] = [];
			for (var row = 0; row < 3; row++) {
				transformRows[row] = document.createElement('tr');
				this.transformTable.appendChild(transformRows[row]);
			}
			var _k = 0;
			for (var col = 0; col < 3; col++) {
				for (var row = 0; row < 3; row++) {
					const k = _k;
					const td = document.createElement('td');
					transformRows[row].appendChild(td);
					const input = document.createElement('input');
					input.size = 5;
					input.maxLength = 5;
					td.appendChild(input);
					input.oninput = () => {
						const parsed = Number(input.value);
						if (parsed !== parsed) {
							return;
						}
						const clamped = Math.min(Math.max(-10, parsed), 10);
						this.transform.elements[k] = clamped;
					};
					input.onblur = () => {
						input.value = this.transform.elements[k].toFixed(2);
					};
					this.transformInputs[k] = input;
					_k++;
				}
			}
		}

		const nodeTable = document.createElement('table');
		nodeTable.classList.add('node-table');
		this.canvasContainer.insertAdjacentElement('afterend', nodeTable);
		{
			const caption = document.createElement('caption');
			nodeTable.appendChild(caption);
			caption.textContent = 'Homogeneous node coordinates';
			caption.style.visibility = 'hidden';
			caption.style.height =
				caption.style.padding =
				caption.style.margin = '0';
		}
		this.headerRow = document.createElement('tr');
		nodeTable.appendChild(this.headerRow);
		this.headerRow.appendChild(document.createElement('td'));
		this.xRow = document.createElement('tr');
		nodeTable.appendChild(this.xRow);
		this.yRow = document.createElement('tr');
		nodeTable.appendChild(this.yRow);
		this.wRow = document.createElement('tr');
		nodeTable.appendChild(this.wRow);
		{
			const thx = document.createElement('th');
			this.xRow.appendChild(thx);
			thx.setAttribute('scope', 'row');
			thx.innerHTML = katex.renderToString('wx');
			const thy = document.createElement('th');
			this.yRow.appendChild(thy);
			thy.setAttribute('scope', 'row');
			thy.innerHTML = katex.renderToString('wy');
			const thw = document.createElement('th');
			this.wRow.appendChild(thw);
			thw.setAttribute('scope', 'row');
			thw.innerHTML = katex.renderToString('w');
		}

		this.viewTable = document.createElement('table');
		this.canvasContainer.insertAdjacentElement('afterend', this.viewTable);
		const viewTableRow = document.createElement('tr');
		viewTableRow.innerHTML = '<th scope="row">View:</th>';
		this.viewTable.appendChild(viewTableRow);

		const xy = document.createElement('button');
		xy.textContent = 'XY';
		xy.onclick = () => { this.viewXY(); };
		const xyTd = document.createElement('td');
		xyTd.appendChild(xy);
		viewTableRow.appendChild(xyTd);
		const yw = document.createElement('button');
		yw.textContent = 'YW';
		yw.onclick = () => { this.viewYW(); };
		const ywTd = document.createElement('td');
		ywTd.appendChild(yw);
		viewTableRow.appendChild(ywTd);
		const iso = document.createElement('button');
		iso.textContent = 'Isometric';
		iso.onclick = () => { this.viewIso(); };
		const isoTd = document.createElement('td');
		isoTd.appendChild(iso);
		viewTableRow.appendChild(isoTd);

		const projectedNodeTable = document.createElement('table');
		{
			const caption = document.createElement('caption');
			projectedNodeTable.appendChild(caption);
			caption.textContent = 'Projected node coordinates';
		}
		this.canvas.appendChild(projectedNodeTable);
		this.projectedHeaderRow = document.createElement('tr');
		projectedNodeTable.appendChild(this.projectedHeaderRow);
		this.projectedHeaderRow.appendChild(document.createElement('td'));
		this.projectedXRow = document.createElement('tr');
		projectedNodeTable.appendChild(this.projectedXRow);
		this.projectedYRow = document.createElement('tr');
		projectedNodeTable.appendChild(this.projectedYRow);
		{
			const thx = document.createElement('th');
			this.projectedXRow.appendChild(thx);
			thx.setAttribute('scope', 'row');
			thx.innerHTML = katex.renderToString('x');
			const thy = document.createElement('th');
			this.projectedYRow.appendChild(thy);
			thy.setAttribute('scope', 'row');
			thy.innerHTML = katex.renderToString('y');
		}

		this.viewIso();
	}

	render(): void {
		this.updateColors();
		this.interaction.update();
		this.updateNodes();
		this.resizeCanvasToDisplaySize();
		this.controls.update();
		this.light.position.copy(this.camera.position);

		const viewDir = new THREE.Vector3().subVectors(this.controls.target, this.camera.position).normalize();
		const opacities = [0, 0, 0];
		for (var axis = 0; axis < 3; axis++) {
			const axisDir = new THREE.Vector3().setComponent(axis, 1);
			const cosAngle = axisDir.dot(viewDir);
			const angle = Math.acos(Math.abs(cosAngle));
			const opacity = Math.max(0, Math.min(5 * (0.5 * Math.PI - angle - 0.025), 1));
			const opacity2 = Math.max(0, Math.min(5 * (angle - 0.025), 1));
			const opacity3 = Math.max(0, Math.min(2 * (angle - 0.05), 1));
			this.gridLineMats[axis].opacity = opacity;
			this.gridLineMats[axis].transparent = opacity != 1;
			this.gridLineMats[axis].depthWrite = opacity == 1;
			this.gridLineMats[axis].visible = opacity != 0;
			if (axis == 2) {
				for (var nodeRepresentation of this.nodeRepresentations) {
					const material = (nodeRepresentation.homogeneous.material as THREE.Material);
					material.opacity = opacity2;
					material.transparent = opacity2 != 1;
					material.depthWrite = opacity2 == 1;
					material.visible = opacity2 != 0;
					const lineMaterial = nodeRepresentation.projectionLine.material;
					lineMaterial.opacity = opacity2;
					lineMaterial.transparent = opacity2 != 1;
					lineMaterial.depthWrite = opacity2 == 1;
					lineMaterial.visible = opacity2 != 0;

					if (nodeRepresentation.transformedHomogeneous) {
						const material = (nodeRepresentation.transformedHomogeneous.material as THREE.Material);
						material.opacity = 0.5 * opacity2;
						const lineMaterial = nodeRepresentation.transformedProjectionLine!.material;
						lineMaterial.opacity = 0.5 * opacity2;
					}
				}

				this.homogeneousSplineMat.opacity = opacity2;
				this.homogeneousSplineMat.transparent = opacity2 != 1;
				this.homogeneousSplineMat.depthWrite = opacity2 == 1;
				this.homogeneousSplineMat.visible = opacity2 != 0;

				this.projectionSurfaceMat.opacity = 0.5 * opacity2;
				this.projectionSurfaceMat.visible = opacity2 != 0;

				this.controlPolyMat.opacity = opacity2;
				if (this.allNodesInPlane == true) {
					this.controlPolyMat.opacity = 1.0;
				}
				this.controlPolyMat.transparent = opacity2 != 1 || !this.allNodesInPlane;
				this.controlPolyMat.depthWrite = this.controlPolyMat.opacity == 1;
				this.controlPolyMat.visible = opacity2 != 0 || this.allNodesInPlane;

				const material = this.manipulators[axis].material as THREE.MeshLambertMaterial;
				material.opacity = opacity2;
				material.transparent = opacity2 != 1;
				material.depthWrite = opacity2 == 1;
				material.visible = opacity2 != 0;
			} else {
				const material = this.manipulators[axis].material as THREE.MeshLambertMaterial;
				material.opacity = opacity3;
				material.transparent = opacity3 != 1;
				material.depthWrite = opacity3 == 1;
				material.visible = opacity3 != 0;
			}
			this.axisMaterials[axis].opacity = opacity2;
			this.axisMaterials[axis].transparent = opacity2 != 1;
			this.axisMaterials[axis].depthWrite = opacity2 == 1;
			this.axisMaterials[axis].visible = opacity2 != 0;
			opacities[axis] = opacity2;
		}

		this.renderer.render(this.scene, this.camera);

		for (var axis = 0; axis < 3; axis++) {
			const label = this.axisLabels[axis];
			const axisDir = new THREE.Vector4(1, 1, 1, 1).setComponent(axis, 11);
			axisDir
				.applyMatrix4(this.camera.matrixWorldInverse)
				.applyMatrix4(this.camera.projectionMatrix);
			const u = axisDir.x / axisDir.w;
			const v = axisDir.y / axisDir.w;
			const ui = Math.ceil(this.canvas.clientWidth * (0.5 * u + 0.5) - label.clientWidth / 2);
			const vi = Math.ceil(this.canvas.clientHeight * (0.5 * v + 0.5) - label.clientHeight / 2);
			label.style.left = ui + 'px';
			label.style.bottom = vi + 'px';
			label.style.opacity = opacities[axis].toString();
		}
	}

	viewXY(): void {
		this.controls.enableDamping = false;
		this.controls.reset();
		this.controls.enableDamping = true;

		// position and target aren't recreated exactly on reset,
		// fix them so that up direction remains constant in XY view
		this.camera.position.x = 12;
		this.camera.position.y = 12;
		this.camera.position.z = 12;
		this.controls.target.x = 6;
		this.controls.target.y = 6;
		this.controls.target.z = 6;

		this.camera.position.x = 6;
		this.camera.position.y = 6;
		this.camera.position.z = 6 + Math.sqrt(3 * 6 * 6);
		this.camera.zoom = 1.6;
		this.camera.updateProjectionMatrix();
	}

	viewYW(): void {
		this.controls.enableDamping = false;
		this.controls.reset();
		this.controls.enableDamping = true;
		this.camera.position.x = 6 + Math.sqrt(3 * 6 * 6);
		this.camera.position.y = 6;
		this.camera.position.z = 6;
		this.camera.zoom = 1.6;
		this.camera.updateProjectionMatrix();
	}

	viewIso(): void {
		this.controls.enableDamping = false;
		this.controls.reset();
		this.controls.enableDamping = true;
		this.camera.zoom = 1.1;
		this.controls.target.z += 1;
		this.camera.position.z += 1;
		this.camera.updateProjectionMatrix();
	}

	private resizeCanvasToDisplaySize(): void {
		const pixelRatio = window.devicePixelRatio || 1.0;
		const width = Math.round(this.canvasContainer.clientWidth * pixelRatio);
		const height = Math.round(this.canvasContainer.clientHeight * pixelRatio);

		if (this.canvas.width !== width || this.canvas.height !== height) {
			this.renderer.setSize(width, height, false);
			this.canvas.style.width = Math.round(width / pixelRatio) + 'px';
			this.canvas.style.height = Math.round(height / pixelRatio) + 'px';

			// update camera
			const size = 6 * Math.sqrt(3);
			const aspect = width / height;
			this.camera.right = Math.max(size, size * aspect);
			this.camera.left = -this.camera.right;
			this.camera.top = Math.max(size, size / aspect);
			this.camera.bottom = -this.camera.top;

			this.camera.updateProjectionMatrix();
		}
	}

	private updateColors(): void {
		const newForeground = colorToRgba(window.getComputedStyle(document.body).getPropertyValue('color'));
		const newBackground = colorToRgba(window.getComputedStyle(document.documentElement).getPropertyValue('background-color'));
		const newBackground2 = colorToRgba(window.getComputedStyle(document.getElementsByClassName('content-wrapper')[0]).getPropertyValue('background-color'));

		if (newForeground !== this.foreground || newBackground !== this.background || newBackground2 !== this.background2) {
			for (var gridLineMat of this.gridLineMats) {
				gridLineMat.color = new THREE.Color(newBackground2);
			}
			this.controlPolyMat.color =
				this.homogeneousSplineMat.color =
				this.projectedSplineMat.color = new THREE.Color(newForeground);
			this.projectionSurfaceMat.color = new THREE.Color(newForeground & 0xff00ff);
			this.renderer.setClearColor(newBackground);
			this.foreground = newForeground;
			this.background = newBackground;
			this.background2 = newBackground2;
		}

		for (var axis = 0; axis < 3; axis++) {
			const newAxisColor = colorToRgba(window.getComputedStyle(this.axisLabels[axis]).getPropertyValue('color'));
			if (newAxisColor !== this.axisColors[axis]) {
				this.axisMaterials[axis].setValues({ color: newAxisColor });
				this.axisColors[axis] = newAxisColor;
			}
		}
	}

	private updateNodes(): void {
		if (this.currentNode >= this.nodeRepresentations.length) {
			for (var manipulator of this.manipulators) {
				manipulator.visible = false;
			}
			this.currentNode = -1;
		}

		var anyNodeChanged = false;
		while (this.nodes.length < this.nodeRepresentations.length) {
			anyNodeChanged = true;
			const nodeRepresentation = this.nodeRepresentations.pop()!;
			this.scene.remove(nodeRepresentation.homogeneous);
			this.scene.remove(nodeRepresentation.projected);
			this.scene.remove(nodeRepresentation.projectionLine);
			if (nodeRepresentation.transformedHomogeneous) {
				this.scene.remove(nodeRepresentation.transformedHomogeneous);
				this.scene.remove(nodeRepresentation.transformedProjected!);
				this.scene.remove(nodeRepresentation.transformedProjectionLine!);
			}
			this.headerRow.removeChild(nodeRepresentation.tableHeader);
			this.xRow.removeChild(nodeRepresentation.xCell);
			this.yRow.removeChild(nodeRepresentation.yCell);
			this.wRow.removeChild(nodeRepresentation.wCell);
			this.projectedHeaderRow.removeChild(nodeRepresentation.projectedTableHeader);
			this.projectedXRow.removeChild(nodeRepresentation.projectedXCell);
			this.projectedYRow.removeChild(nodeRepresentation.projectedYCell);

			this.interaction.remove(nodeRepresentation.homogeneous);
		}
		while (this.nodes.length > this.nodeRepresentations.length) {
			const i = this.nodeRepresentations.length;
			anyNodeChanged = true;
			const nodeColor = this.nodeColors[this.nodeRepresentations.length % this.nodeColors.length];
			const nodeMaterial = new THREE.MeshLambertMaterial({ color: nodeColor });
			const nodeMaterial2 = nodeMaterial.clone() as THREE.MeshLambertMaterial;
			const projectionLineMaterial = new LineMaterial({ color: nodeColor, linewidth: 0.005, resolution: new THREE.Vector2(1.2, 0.8) });
			const nodeRepresentation = {
				homogeneous: new THREE.Mesh(this.sphereGeometry, nodeMaterial),
				projected: new THREE.Mesh(this.sphereGeometry, nodeMaterial2),
				projectionLine: new Line2(undefined, projectionLineMaterial),
				tableHeader: document.createElement('th'),
				xCell: document.createElement('td'),
				yCell: document.createElement('td'),
				wCell: document.createElement('td'),
				projectedTableHeader: document.createElement('th'),
				projectedXCell: document.createElement('td'),
				projectedYCell: document.createElement('td'),
			};
			this.nodeRepresentations.push(nodeRepresentation);
			this.scene.add(nodeRepresentation.homogeneous);
			this.scene.add(nodeRepresentation.projected);
			this.scene.add(nodeRepresentation.projectionLine);
			this.headerRow.appendChild(nodeRepresentation.tableHeader);
			this.xRow.appendChild(nodeRepresentation.xCell);
			this.yRow.appendChild(nodeRepresentation.yCell);
			this.wRow.appendChild(nodeRepresentation.wCell);
			this.projectedHeaderRow.appendChild(nodeRepresentation.projectedTableHeader);
			this.projectedXRow.appendChild(nodeRepresentation.projectedXCell);
			this.projectedYRow.appendChild(nodeRepresentation.projectedYCell);

			{
				const xCellInput = document.createElement('input');
				xCellInput.size = 5;
				xCellInput.maxLength = 5;
				nodeRepresentation.xCell.appendChild(xCellInput);
				xCellInput.oninput = () => {
					const parsed = Number(xCellInput.value);
					if (this.activeManipulator && this.currentNode === i || parsed !== parsed) {
						return;
					}
					const clamped = Math.min(Math.max(0, parsed), 20);
					this.nodes[i].x = clamped;
				};
				xCellInput.onblur = () => {
					xCellInput.value = this.nodes[i].x.toFixed(2);
				};
			}
			{
				const yCellInput = document.createElement('input');
				yCellInput.size = 5;
				yCellInput.maxLength = 5;
				nodeRepresentation.yCell.appendChild(yCellInput);
				yCellInput.oninput = () => {
					const parsed = Number(yCellInput.value);
					if (this.activeManipulator && this.currentNode === i || parsed !== parsed) {
						return;
					}
					const clamped = Math.min(Math.max(0, parsed), 20);
					this.nodes[i].y = clamped;
				};
				yCellInput.onblur = () => {
					yCellInput.value = this.nodes[i].y.toFixed(2);
				};
			}
			{
				const wCellInput = document.createElement('input');
				wCellInput.disabled = !this._cameraControlsEnabled;
				wCellInput.size = 5;
				wCellInput.maxLength = 5;
				nodeRepresentation.wCell.appendChild(wCellInput);
				wCellInput.oninput = () => {
					const parsed = Number(wCellInput.value);
					if (this.activeManipulator && this.currentNode === i || parsed !== parsed) {
						return;
					}
					const clamped = Math.min(Math.max(0.25, parsed), 20);
					this.nodes[i].z = clamped;
				};
				wCellInput.onblur = () => {
					wCellInput.value = this.nodes[i].z.toFixed(2);
				};
			}

			nodeRepresentation.tableHeader.innerHTML = katex.renderToString('\\mathbf{p}_{' + (i + 1) + ',H}');
			nodeRepresentation.projectedTableHeader.innerHTML = katex.renderToString('\\mathbf{p}_{' + (i + 1) + '}');

			const nodeColorC = new THREE.Color(nodeColor);
			const foregroundC = new THREE.Color(this.foreground);
			const mixed = new THREE.Color();
			mixed.lerpColors(nodeColorC, foregroundC, 0.5);

			for (var _repr of [nodeRepresentation.homogeneous, nodeRepresentation.projected, nodeRepresentation.projectionLine]) {
				const repr = _repr;
				this.interaction.add(repr);
				repr.addEventListener('mouseover', (e) => {
					const ie = e as InteractiveEvent;
					ie.stopPropagation();
					nodeMaterial.color = mixed;
					nodeMaterial2.color = mixed;
					projectionLineMaterial.color = mixed;
				});
				repr.addEventListener('mouseout', () => {
					nodeMaterial.color = nodeColorC;
					nodeMaterial2.color = nodeColorC;
					projectionLineMaterial.color = nodeColorC;
				});
				repr.addEventListener('click', (e) => {
					const ie = e as InteractiveEvent;
					ie.stopPropagation();
					if (this.currentNode !== i) {
						this.currentNode = i;
						for (var manipulator of this.manipulators) {
							manipulator.visible = true;
						}
						this.manipulators[0].position.copy(nodeRepresentation.projected.position);
						this.manipulators[1].position.copy(nodeRepresentation.projected.position);
						this.manipulators[2].position.copy(nodeRepresentation.homogeneous.position);
						const rotation = new THREE.Quaternion();
						rotation.setFromUnitVectors(new THREE.Vector3(0, 1, 0), this.nodes[i].clone().normalize());
						this.manipulators[2].setRotationFromQuaternion(rotation);
					} else {
						for (var manipulator of this.manipulators) {
							manipulator.visible = false;
						}
						this.currentNode = -1;
					}
				});
				repr.addEventListener('mousedown', (e) => {
					const ie = e as InteractiveEvent;
					const target = ie.target as THREE.Mesh;
					if (!target.visible || !(target.material as THREE.MeshLambertMaterial).visible) {
						return;
					}
					ie.stopPropagation();
					this.controls.enabled = false;
				});
				repr.addEventListener('mouseup', () => { this.controls.enabled = this._cameraControlsEnabled; });
			}
		}

		const updateTransform = !this.oldTransform.equals(this.transform);
		const isIdentity = this.transform.equals(new THREE.Matrix3());

		if (updateTransform) {
			for (var k = 0; k < 9; k++) {
				if (!document.hasFocus() || document.activeElement !== this.transformInputs[k]) {
					this.transformInputs[k].value = this.transform.elements[k].toFixed(2);
				}
			}
		}

		for (var i = 0; i < this.nodes.length; i++) {
			const nodeRepresentation = this.nodeRepresentations[i];
			const doTransforms = () => {
				if (isIdentity) {
					if (nodeRepresentation.transformedHomogeneous) {
						nodeRepresentation.transformedHomogeneous.visible = false;
						nodeRepresentation.transformedProjected!.visible = false;
						nodeRepresentation.transformedProjectionLine!.visible = false;
					}
				} else {
					if (!nodeRepresentation.transformedHomogeneous) {
						const nodeColor = this.nodeColors[i % this.nodeColors.length];
						const nodeMaterial = new THREE.MeshLambertMaterial({ color: nodeColor, opacity: 0.5, transparent: true, depthWrite: false });
						const nodeMaterial2 = nodeMaterial.clone() as THREE.MeshLambertMaterial;
						const projectionLineMaterial = new LineMaterial({ color: nodeColor, linewidth: 0.005, resolution: new THREE.Vector2(1.2, 0.8), opacity: 0.5, transparent: true, depthWrite: false });
						nodeRepresentation.transformedHomogeneous = new THREE.Mesh(this.sphereGeometry, nodeMaterial);
						nodeRepresentation.transformedProjected = new THREE.Mesh(this.sphereGeometry, nodeMaterial2);
						nodeRepresentation.transformedProjectionLine = new Line2(undefined, projectionLineMaterial);
						this.scene.add(nodeRepresentation.transformedHomogeneous);
						this.scene.add(nodeRepresentation.transformedProjected);
						this.scene.add(nodeRepresentation.transformedProjectionLine);
					}
					nodeRepresentation.transformedHomogeneous.visible = true;
					nodeRepresentation.transformedProjected!.visible = true;
					nodeRepresentation.transformedProjectionLine!.visible = true;

					const transformed = this.nodes[i].clone().applyMatrix3(this.transform);
					nodeRepresentation.transformedHomogeneous.position.copy(transformed);
					nodeRepresentation.transformedProjected!.position.copy(transformed.clone().divideScalar(transformed.z));

					const projectionEnd = transformed.clone();
					projectionEnd.divideScalar(
						Math.max(projectionEnd.x, projectionEnd.y, projectionEnd.z)
					);
					projectionEnd.multiplyScalar(10);
					nodeRepresentation.transformedProjectionLine!.geometry.setPositions([
						0, 0, 0,
						projectionEnd.x, projectionEnd.y, projectionEnd.z,
					]);
				}
			};
			if (nodeRepresentation.homogeneous.position.equals(this.nodes[i])) {
				if (updateTransform) {
					doTransforms();
				}
				continue;
			}
			anyNodeChanged = true;

			doTransforms();

			nodeRepresentation.homogeneous.position.copy(this.nodes[i]);
			if (!document.hasFocus() || document.activeElement !== nodeRepresentation.xCell.firstElementChild) {
				(nodeRepresentation.xCell.firstElementChild! as HTMLInputElement).value = this.nodes[i].x.toFixed(2);
			}
			if (!document.hasFocus() || document.activeElement !== nodeRepresentation.yCell.firstElementChild) {
				(nodeRepresentation.yCell.firstElementChild! as HTMLInputElement).value = this.nodes[i].y.toFixed(2);
			}
			if (!document.hasFocus() || document.activeElement !== nodeRepresentation.wCell.firstElementChild) {
				(nodeRepresentation.wCell.firstElementChild! as HTMLInputElement).value = this.nodes[i].z.toFixed(2);
			}

			const projected = this.nodes[i].clone();
			projected.divideScalar(projected.z);
			nodeRepresentation.projected.position.copy(projected);

			nodeRepresentation.projectedXCell.textContent = projected.x.toFixed(2);
			nodeRepresentation.projectedYCell.textContent = projected.y.toFixed(2);

			const projectionEnd = this.nodes[i].clone();
			projectionEnd.divideScalar(
				Math.max(projectionEnd.x, projectionEnd.y, projectionEnd.z)
			);
			projectionEnd.multiplyScalar(10);

			nodeRepresentation.projectionLine.geometry.setPositions([
				0, 0, 0,
				projectionEnd.x, projectionEnd.y, projectionEnd.z,
			]);

			if (this.currentNode === i) {
				this.manipulators[0].position.copy(projected);
				this.manipulators[1].position.copy(projected);
				this.manipulators[2].position.copy(this.nodes[i]);
				const rotation = new THREE.Quaternion();
				rotation.setFromUnitVectors(new THREE.Vector3(0, 1, 0), this.nodes[i].clone().normalize());
				this.manipulators[2].setRotationFromQuaternion(rotation);
			}
		}

		this.oldTransform.copy(this.transform);

		if (anyNodeChanged) {
			if (this.controlPolygon === undefined) {
				if (this.nodes.length <= 1) {
					return;
				}
				this.controlPolygon = new Line2(undefined, this.controlPolyMat);
				this.homogeneousSpline = new Line2(undefined, this.homogeneousSplineMat);
				this.projectedSpline = new Line2(undefined, this.projectedSplineMat);
				this.projectionSurface = new THREE.Mesh(undefined, this.projectionSurfaceMat);
				this.scene.add(this.controlPolygon);
				this.scene.add(this.homogeneousSpline);
				this.scene.add(this.projectedSpline);
				this.scene.add(this.projectionSurface);
			}
			if (this.nodes.length <= 1) {
				this.controlPolygon.visible = false;
				this.homogeneousSpline!.visible = false;
				this.projectedSpline!.visible = false;
				this.projectionSurface!.visible = false;
				return;
			}

			const flatNodes: number[] = [];
			for (var node of this.nodes) {
				node.toArray(flatNodes, flatNodes.length);
			}
			this.controlPolygon.geometry.setPositions(flatNodes);
			this.controlPolygon.computeLineDistances();

			this.allNodesInPlane = true;
			const splinePositions = evaluateBSpline(this.nodes);
			this.homogeneousSpline!.geometry.setPositions(splinePositions);
			this.homogeneousSpline!.computeLineDistances();

			{
				const geometry = this.projectionSurface!.geometry;
				const numTriangles = (splinePositions.length / 3 | 0) - 1;
				const positions = new Float32Array(splinePositions.length + 3 * numTriangles);
				for (var i = 0; i < splinePositions.length; i++) {
					positions[i] = splinePositions[i];
				}
				for (var i = 0; i < numTriangles; i++) {
					positions[splinePositions.length + 3 * i + 0] =
						positions[splinePositions.length + 3 * i + 1] =
						positions[splinePositions.length + 3 * i + 2] = 0;
				}
				const indices: number[] = [];
				for (var i = 0; i < numTriangles; i++) {
					indices[3 * i + 0] = numTriangles + 1 + i;
					indices[3 * i + 1] = i;
					indices[3 * i + 2] = i + 1;
				}
				geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
				geometry.setIndex(indices);
				geometry.computeVertexNormals();
				geometry.computeBoundingBox();
			}

			for (var i = 0; i < splinePositions.length; i++) {
				const k = 3 * ((i / 3) | 0) + 2;
				this.allNodesInPlane = this.allNodesInPlane && (splinePositions[k] == 1);
				splinePositions[i] /= splinePositions[k];
			}
			this.projectedSpline!.geometry.setPositions(splinePositions);
		}
	}
}

renderMathInElement(document.body);

const diagram1 = new InteractiveDiagram(document.getElementById('canvas-1') as HTMLCanvasElement);
const diagram2 = new InteractiveDiagram(document.getElementById('canvas-2') as HTMLCanvasElement);
const diagram3 = new InteractiveDiagram(document.getElementById('canvas-3') as HTMLCanvasElement);
const diagram4 = new InteractiveDiagram(document.getElementById('canvas-4') as HTMLCanvasElement);

diagram1.nodes.push(new THREE.Vector3(6, 4, 2));
diagram2.nodes.push(new THREE.Vector3(6, 4, 2));
for (var diagram of [diagram3, diagram4]) {
	diagram.nodes.push(new THREE.Vector3(0, 10, 1));
	diagram.nodes.push(new THREE.Vector3(1, 2, 1));
	diagram.nodes.push(new THREE.Vector3(7, 3, 1));
	diagram.nodes.push(new THREE.Vector3(8, 0, 1));
	diagram.nodes.push(new THREE.Vector3(10, 9, 1));
	diagram.nodes.push(new THREE.Vector3(7, 7, 1));
}
diagram4.nodes[1].set(5, 10, 5);
diagram4.nodes[3].set(16, 0, 2);

diagram1.viewXY();
diagram2.viewXY();
diagram3.viewXY();
diagram3.cameraControlsEnabled = false;
diagram4.viewXY();

// elements are column-major!
diagram2.transform.elements = [
	1, 0, 0,
	0, 1, 0,
	1, 1, 1,
];
diagram2.showTransform = true;

function animate(): void {
	requestAnimationFrame(animate);
	diagram1.render();
	diagram2.render();
	diagram3.render();
	diagram4.render();
}

animate();
